import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, XCircle, ExternalLink, Loader2, Search, X } from "lucide-react";
import { api, ApiError, type AdminPayment } from "../../lib/api";
import { StatusBadge } from "../../components/ui/Badges";
import { AdminEmptyState, AdminFilterBar, AdminPageHeader, AdminTabBar } from "../../components/admin/AdminUI";
import { Modal } from "../../components/ui/Modal";
import { useAdminToken, PageLoader, PageError } from "./adminShared";
import { ConfirmDialog } from "../../components/admin/AdminModals";

const PAYMENT_REJECTION_REASONS = [
  "Incorrect amount",
  "Transaction ID not found",
  "Duplicate / already claimed",
  "Wrong recipient account",
  "Expired transaction",
  "Other reason",
];

export function AdminPayments() {
  const token = useAdminToken();
  const [activeTab, setActiveTab] = useState("All");
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ text: string; type: "success" | "info" } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    payment: AdminPayment | null;
    action: "approve" | "reject";
  }>({ open: false, payment: null, action: "approve" });
  const [deciding, setDeciding] = useState(false);

  const [rejectModal, setRejectModal] = useState<{
    open: boolean;
    targetId: string;
    user?: string;
    isManualPending?: boolean;
  }>({ open: false, targetId: "" });
  const [selectedReason, setSelectedReason] = useState<string>(PAYMENT_REJECTION_REASONS[0]);
  const [customReason, setCustomReason] = useState<string>("");
  const [rejecting, setRejecting] = useState(false);

  const tabs = ["All", "Pending review", "Pending", "Confirmed", "Failed"];

  const filteredPayments = payments.filter((p) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (p.id && p.id.toLowerCase().includes(q)) ||
      (p.txnId && p.txnId.toLowerCase().includes(q)) ||
      (p.user && p.user.toLowerCase().includes(q)) ||
      (p.plan && p.plan.toLowerCase().includes(q)) ||
      (p.amount && p.amount.toLowerCase().includes(q)) ||
      (p.provider && p.provider.toLowerCase().includes(q)) ||
      (p.date && p.date.toLowerCase().includes(q))
    );
  });

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.admin.payments(token, activeTab)
      .then((data) => setPayments(data.items))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load payments"))
      .finally(() => setLoading(false));
  }, [token, activeTab]);

  useEffect(() => { load(); }, [load]);

  async function handleConfirm(txRef: string) {
    if (!token) return;
    setConfirming(txRef);
    setError("");
    setNotice(null);
    try {
      await api.admin.confirmPayment(token, txRef);
      setNotice({ text: "Payment confirmed successfully. Premium has been granted.", type: "success" });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Confirm failed");
    } finally {
      setConfirming(null);
    }
  }

  function handleReject(txRef: string) {
    setRejectTarget(txRef);
  }

  async function executeReject(txRef: string) {
    if (!token) return;
    setCancelling(txRef);
    setError("");
    setNotice(null);
    try {
      await api.admin.rejectPayment(token, txRef, "Admin cancelled manual verification");
      setNotice({ text: "Manual payment verification cancelled.", type: "info" });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to cancel payment");
    } finally {
      setCancelling(null);
      setRejectTarget(null);
    }
  }

  async function handleCheck(txRef: string) {
    if (!token) return;
    setChecking(txRef);
    setError("");
    setNotice(null);
    try {
      const res = await api.admin.checkPayment(token, txRef);
      if (res.ok) {
        setNotice({ text: res.message || "Payment verified and confirmed by gateway.", type: "success" });
      } else {
        setNotice({ text: res.message || "Payment check: still pending or unverified.", type: "info" });
      }
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Payment check failed");
    } finally {
      setChecking(null);
    }
  }

  async function executePaymentRejection() {
    if (!token || !rejectModal.targetId) return;
    const finalReason = selectedReason === "Other reason" ? customReason.trim() : selectedReason;
    if (selectedReason === "Other reason" && !finalReason) {
      setError("Please specify the custom rejection reason.");
      return;
    }
    setRejecting(true);
    setError("");
    setNotice(null);
    try {
      if (rejectModal.isManualPending) {
        await api.admin.rejectPayment(token, rejectModal.targetId, finalReason);
      } else {
        await api.admin.decidePayment(token, rejectModal.targetId, "reject", finalReason);
      }
      setNotice({ text: "Payment rejected successfully.", type: "success" });
      setRejectModal({ open: false, targetId: "" });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to reject payment");
    } finally {
      setRejecting(false);
    }
  }

  async function executeDecision() {
    if (!token || !decisionDialog.payment) return;
    setDeciding(true);
    setError("");
    setNotice(null);
    const targetId = decisionDialog.payment.paymentId || decisionDialog.payment.id;
    const action = decisionDialog.action;
    try {
      const res = await api.admin.decidePayment(token, targetId, action);
      if (res.already_decided) {
        setNotice({ text: "This payment was already decided.", type: "info" });
      } else {
        setNotice({
          text: `Payment ${action === "approve" ? "approved" : "rejected"} successfully.`,
          type: "success",
        });
      }
      setDecisionDialog({ open: false, payment: null, action: "approve" });
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Failed to ${action} payment`);
    } finally {
      setDeciding(false);
    }
  }

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader title="Payments" subtitle="Review and confirm student transactions" />
      {error && <PageError message={error} />}
      {notice && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${notice.type === "success"
            ? "border-success/30 bg-success/10 text-success"
            : "border-info/30 bg-info/10 text-info"
          }`}>
          <span>{notice.type === "success" ? "✓" : "ℹ"}</span>
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="ml-auto text-xs hover:opacity-75">✕</button>
        </div>
      )}
      <AdminTabBar
        active={activeTab}
        onChange={setActiveTab}
        tabs={tabs.map((t) => ({ id: t, label: t }))}
      />
      <AdminFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search payments by student name, transaction ID, plan..."
        showClear={Boolean(search)}
        onClear={() => setSearch("")}
      />
      {loading ? <PageLoader /> : (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted">
                  {["Transaction ID", "Student", "Semester", "Package", "Amount", "Method", "Receipt", "Date", "Status", "Action"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredPayments.length === 0 ? (
                  <tr><td colSpan={10}><AdminEmptyState message={search ? "No payments matching your search" : "No payments in this category"} /></td></tr>
                ) : filteredPayments.map((p) => {
                  const isPendingReview = p.status === "Pending Review" || p.status === "Pending review";
                  const receiptLink = p.receiptUrl || `https://transactioninfo.ethiotelecom.et/receipt/${p.txnId || p.id}`;
                  const providerName = (p.provider || "manual").toLowerCase();
                  return (
                    <tr key={p.id} className="hover:bg-muted/50 transition">
                      <td className="px-4 py-3.5 text-sm font-mono text-muted-foreground">
                        <div>{p.txnId || p.id}</div>
                        {p.reviewReason && (
                          <div className="text-xs text-warning font-sans mt-0.5">{p.reviewReason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm">
                        <div className="font-semibold text-foreground">{p.user}</div>
                      </td>
                      <td className="px-4 py-3.5 text-sm">
                        {p.semester ? (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                            Sem {p.semester}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-foreground font-medium">
                        {p.packageName || p.plan}
                      </td>
                      <td className="px-4 py-3.5 text-sm font-bold">{p.amount}</td>
                      <td className="px-4 py-3.5 text-xs">
                        <span className="px-2 py-0.5 rounded font-mono font-semibold uppercase text-xs bg-secondary text-foreground border border-border">
                          {providerName}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm">
                        <a
                          href={receiptLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                        >
                          Receipt <ExternalLink size={12} />
                        </a>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-muted-foreground">{p.date}</td>
                      <td className="px-4 py-3.5"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3.5">
                        {isPendingReview ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setDecisionDialog({ open: true, payment: p, action: "approve" })}
                              disabled={deciding}
                              className="text-xs font-semibold text-primary bg-secondary px-3 py-1.5 rounded-full hover:bg-primary hover:text-on-accent transition disabled:opacity-50 flex items-center gap-1"
                            >
                              <Check size={12} /> Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRejectModal({
                                  open: true,
                                  targetId: p.paymentId || p.id,
                                  user: p.user,
                                  isManualPending: false,
                                });
                                setSelectedReason(PAYMENT_REJECTION_REASONS[0]);
                                setCustomReason("");
                              }}
                              disabled={deciding}
                              className="text-xs font-semibold text-destructive bg-destructive/10 px-3 py-1.5 rounded-full hover:bg-destructive hover:text-destructive-foreground transition disabled:opacity-50 flex items-center gap-1"
                            >
                              <X size={12} /> Reject
                            </button>
                          </div>
                        ) : p.status === "Pending" ? (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleCheck(p.id)}
                              disabled={checking === p.id || confirming === p.id || cancelling === p.id}
                              className="text-xs font-semibold text-muted-foreground bg-muted px-3 py-1.5 rounded-full hover:bg-secondary transition disabled:opacity-50"
                            >
                              {checking === p.id ? "Checking…" : "Check"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleConfirm(p.id)}
                              disabled={confirming === p.id || checking === p.id || cancelling === p.id}
                              className="text-xs font-semibold text-primary bg-secondary px-3 py-1.5 rounded-full hover:bg-primary hover:text-on-accent transition disabled:opacity-50 flex items-center gap-1"
                            >
                              {confirming === p.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setRejectModal({
                                  open: true,
                                  targetId: p.id,
                                  user: p.user,
                                  isManualPending: true,
                                });
                                setSelectedReason(PAYMENT_REJECTION_REASONS[0]);
                                setCustomReason("");
                              }}
                              disabled={cancelling === p.id || confirming === p.id || checking === p.id}
                              className="text-xs font-semibold text-destructive bg-destructive/10 px-3 py-1.5 rounded-full hover:bg-destructive hover:text-destructive-foreground transition disabled:opacity-50 flex items-center gap-1"
                              title="Cancel / Reject manual verification"
                            >
                              {cancelling === p.id ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                              Cancel
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      <ConfirmDialog
        open={decisionDialog.open && decisionDialog.action === "approve"}
        title="Approve payment?"
        message={`Are you sure you want to approve this payment of ${decisionDialog.payment?.amount || ""} for ${decisionDialog.payment?.user || "the student"}? This will grant Premium access immediately.`}
        confirmLabel="Approve"
        danger={false}
        loading={deciding}
        onConfirm={executeDecision}
        onClose={() => setDecisionDialog({ open: false, payment: null, action: "approve" })}
      />

      {/* Option-based Rejection Modal */}
      <Modal
        isOpen={rejectModal.open}
        onClose={() => setRejectModal({ open: false, targetId: "" })}
        title="Reject payment"
        description={`Select a reason for rejecting the payment for ${rejectModal.user || "the student"}.`}
        size="sm"
      >
        <div className="space-y-4">

          <div className="space-y-2 py-1">
            <label className="text-xs font-medium text-foreground block">Rejection reason</label>
            <div className="space-y-2">
              {PAYMENT_REJECTION_REASONS.map((r) => (
                <label
                  key={r}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-xs cursor-pointer transition ${selectedReason === r
                      ? "border-primary bg-primary/5 text-foreground font-medium"
                      : "border-border text-muted-foreground hover:border-muted hover:text-foreground"
                    }`}
                >
                  <input
                    type="radio"
                    name="rejectionReason"
                    value={r}
                    checked={selectedReason === r}
                    onChange={() => setSelectedReason(r)}
                    className="text-primary focus:ring-primary h-3.5 w-3.5"
                  />
                  <span>{r}</span>
                </label>
              ))}
            </div>

            {selectedReason === "Other reason" && (
              <div className="pt-2 animate-in fade-in duration-150">
                <label className="text-xs font-medium text-muted-foreground block mb-1">
                  Specify custom reason
                </label>
                <textarea
                  rows={2}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Enter the specific reason for rejecting this payment..."
                  className="w-full text-xs p-2.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                  autoFocus
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => setRejectModal({ open: false, targetId: "" })}
              disabled={rejecting}
              className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={executePaymentRejection}
              disabled={rejecting || (selectedReason === "Other reason" && !customReason.trim())}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive transition disabled:opacity-50 flex items-center gap-1.5"
            >
              {rejecting && <Loader2 size={12} className="animate-spin" />}
              Reject payment
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
